import { BenefitPlanTypeEnum } from "@platform/openapi-types";

/** A requested plan group's SBS page, as a "Move Plan Group" destination. */
export interface MovePlanDestinationPage {
  id: string;
  productType?: string;
}

// The btx enum for product_type is the canonical plan types plus "notDisclosed"
// (ent's withNotDisclosed), which the generated frontend BenefitPlanTypeEnum does
// not declare — so this compares strings rather than enum members.
const NOT_DISCLOSED = "notDisclosed";

/**
 * True for a product type that states no network/product shape — a carrier response
 * with no plan type, a group whose plans disagreed on one, or a coverage where the
 * concept doesn't apply at all (life, disability).
 */
export const isUnspecifiedProductType = (
  productType: string | undefined,
): boolean =>
  productType == null ||
  productType === "" ||
  productType === BenefitPlanTypeEnum.notApplicable ||
  productType === NOT_DISCLOSED;

// The raw v5 coverage type ("dental") and the frontend's split UI tabs
// ("dental-ppo"/"dental-hmo", see coverageTypeNormalizer.ts) are the same backend
// coverage under three spellings — callers pass whichever their layer holds.
const DENTAL_COVERAGE_TYPES = new Set(["dental", "dental-ppo", "dental-hmo"]);

/**
 * True for a coverage where product type is a real, analyst-meaningful network
 * distinction — currently only dental (ppo vs hmo design). Everywhere else it's
 * either not populated by the RFP flow or interchangeable (life, disability), so
 * there's no shape for an untyped quote to guess wrong.
 */
export const isDentalCoverageType = (
  coverageType: string | undefined,
): boolean => coverageType != null && DENTAL_COVERAGE_TYPES.has(coverageType);

/**
 * The requested pages a carrier's plan group may be moved onto.
 *
 * A group that states its product type may only move to a page of that same type:
 * dental requests a ppo and an hmo design side by side, and a ppo quote never belongs
 * on the hmo one. A group that states none may move to any of them, except on dental
 * (see {@link isDentalCoverageType}) — the one coverage where guessing which design an
 * untyped quote belongs to would be picking a shape on the analyst's behalf.
 *
 * The btx move endpoint enforces the same rule (validateMoveTarget) and leaves the moved
 * group's product_type untouched, so a page this omits would 400. It is the single
 * definition for both the carrier header's "Move Plan Group" gate and the dialog's destination
 * list — those were separate copies, and they drifted.
 *
 * @param requestedPages every requested plan group of the coverage, never the synthetic
 *   Unmapped Plans page.
 * @param excludePageId the page already on screen, dropped from the result. Omit to keep
 *   it (the dialog lists it, disabled, to show where the plan sits today).
 */
export const movePlanDestinations = <T extends MovePlanDestinationPage>(
  quotedProductType: string | undefined,
  requestedPages: readonly T[],
  coverageType: string | undefined,
  excludePageId?: string,
): T[] => {
  const acceptsUnspecified =
    isUnspecifiedProductType(quotedProductType) &&
    !isDentalCoverageType(coverageType);

  return requestedPages.filter(
    (page) =>
      page.id !== excludePageId &&
      (acceptsUnspecified || page.productType === quotedProductType),
  );
};
